/**
 * One chart's data, for as long as something is drawing it.
 *
 * ## Why this is a hook and not more state in `App.tsx`
 *
 * Everything here was a `useState` in the screen component, which meant the
 * app could hold exactly one chart's worth of it. A workspace of four charts
 * on four symbols is not a bigger version of that — it is a different shape,
 * and no amount of care in `App.tsx` produces it while `bars` is a variable
 * belonging to the whole screen.
 *
 * As a hook the same code is instantiable. Four panes call it four times and
 * each gets its own series, its own loading flag, its own staleness. Nothing
 * below is new behaviour; it is the same effects with the screen taken out
 * from under them, and the tests that come with it exist because that claim
 * is easy to make and easy to get wrong.
 *
 * ## What it deliberately does not own
 *
 * The socket. `useSymbolFeed` subscribes this instance to one symbol on the
 * app's single connection — see `marketFeed.tsx` for why there is only one.
 * Four charts do not open four sockets.
 *
 * And errors. A failed load is handed to `onError` rather than held here,
 * because the screen already has one error banner fed from several places and
 * a second source of truth for it would have to be merged back anyway.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { blanksChart } from '@aegis/api-client';
import { applyBar, applyTick } from '@aegis/api-client';
import { api, type Analysis, type Bar, type MarketStatus } from './api.js';
import { useSymbolFeed } from './marketFeed.js';

export interface ChartDataOptions {
  readonly symbol: string;
  readonly timeframe: string;
  /** How many bars to request. The screen asks for more than a pane needs. */
  readonly limit?: number;
  /**
   * A load failed and the message is worth showing.
   *
   * Read through a ref, so a caller may pass a fresh closure every render
   * without causing a refetch.
   */
  readonly onError?: (message: string) => void;
}

export interface ChartData {
  readonly bars: Bar[];
  /** Seconds between bars, measured rather than parsed from the timeframe. */
  readonly barSeconds: number;
  /** The last two daily bars — the reference for net change, nothing else. */
  readonly daily: Bar[];
  readonly lastPrice: number | null;
  readonly delayedByMs: number;
  readonly dataSource: string;
  readonly dataReason: string;
  readonly marketStatus: MarketStatus | null;
  /** A bars request is in flight for the series on screen. */
  readonly loading: boolean;
  readonly analysis: Analysis | null;
  readonly disclaimer: string;
  /**
   * For the on-demand refresh, which is a user action with its own quota and
   * belongs to the screen rather than to the series.
   */
  readonly setAnalysis: (a: Analysis | null) => void;
}

export function useChartData(options: ChartDataOptions): ChartData {
  const { symbol, timeframe, limit = 400 } = options;

  const [bars, setBars] = useState<Bar[]>([]);
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [daily, setDaily] = useState<Bar[]>([]);
  const [loading, setLoading] = useState(false);
  const [delayedByMs, setDelayedByMs] = useState(0);
  const [dataSource, setDataSource] = useState<string>('mock');
  const [dataReason, setDataReason] = useState('');
  const [marketStatus, setMarketStatus] = useState<MarketStatus | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [disclaimer, setDisclaimer] = useState('');

  const onError = useRef(options.onError);
  useEffect(() => { onError.current = options.onError; });

  /**
   * The `symbol:timeframe` whose candles are currently drawn.
   *
   * A ref rather than state: it is read to decide whether to blank, and a
   * render triggered by writing it would be a render caused by bookkeeping.
   */
  const drawnKey = useRef<string | null>(null);

  const barSeconds = useMemo(() => {
    // Two adjacent bars give the real spacing, which beats parsing the
    // timeframe string and is right even for an irregular series.
    if (bars.length < 2) return 300;
    return Math.max(1, bars[bars.length - 1]!.time - bars[bars.length - 2]!.time);
  }, [bars]);

  useSymbolFeed(symbol, {
    onTick: (m) => {
      setLastPrice(m.p);
      setDelayedByMs(m.d);
      // Move the forming candle, not just the number beside the chart.
      //
      // A tick used to update `lastPrice` and nothing else, and `onBar` applied
      // only bars the server marked complete — so the newest candle stayed
      // exactly as it was drawn until its timeframe elapsed. On a 15m chart
      // that is a quarter of an hour of price action the chart knew about and
      // did not show.
      setBars((prev) => [...applyTick(prev, m.p, Math.floor(m.ts / 1000), timeframe)]);
    },
    onBar: (m) => {
      // Both complete and forming bars. The server's version is authoritative
      // — real volume, a close agreed with the venue — so it replaces whatever
      // the local tick merge built.
      setBars((prev) => [...applyBar(prev, m.bar)].slice(-600));
    },
    onAnalysis: (m) => setAnalysis(m.analysis),
    onStatus: (m) => setMarketStatus(m.status),
  });

  useEffect(() => {
    // Empty for the render between mount and whatever resolves a default.
    // Fetching then asks the API for the empty string, which is not a symbol —
    // it produced a 404 on bars and, until the route validated its input, a 500
    // and a crash report on analysis.
    if (!symbol) return undefined;

    /*
      Blank for a different INSTRUMENT, never for a different timeframe.

      The gap this closes was real: `bars` held the previous symbol's candles
      until the new ones arrived, so switching from CL to EURUSD drew CL's
      candles, CL's price axis and CL's AI levels under the EURUSD header.
      `cancelled` stopped a late reply winning; nothing stopped the old series
      being displayed under the new name in the meantime.

      A timeframe change is not that — same instrument, another aggregation —
      so those candles stay, dimmed, rather than flashing a loader on the
      control people touch most.
    */
    const key = `${symbol}:${timeframe}`;
    if (blanksChart(drawnKey.current, key)) {
      setBars([]);
      setAnalysis(null);
      setDelayedByMs(0);
    }
    drawnKey.current = key;
    setLoading(true);

    let cancelled = false;
    void (async () => {
      try {
        const res = await api.bars(symbol, timeframe, limit);
        if (cancelled) return;
        setBars(res.bars);
        setLastPrice(res.quote?.last ?? null);
        setDelayedByMs(res.delayedByMs);
        setDataSource(res.dataSource);
        setDataReason(res.dataReason);
        setMarketStatus(res.marketStatus);
      } catch (e) {
        if (!cancelled) {
          onError.current?.(e instanceof Error ? e.message : 'Failed to load bars.');
        }
      } finally {
        // `finally`, not the end of `try` — a refused load must stop claiming
        // to be in flight, or the candles stay dimmed under an unread error.
        if (!cancelled) setLoading(false);
      }
      try {
        const a = await api.analysis(symbol);
        if (!cancelled) { setAnalysis(a.analysis); setDisclaimer(a.disclaimer); }
      } catch {
        if (!cancelled) setAnalysis(null);
      }
    })();
    return () => { cancelled = true; };
  }, [symbol, timeframe, limit]);

  /**
   * The session reference for net change, fetched per symbol.
   *
   * Keyed on the symbol alone: switching 5m to 15m does not change what a day
   * is. Failure is silent and total — the header omits the change rather than
   * showing one measured from nothing, because a chart that loads is worth more
   * than a change figure.
   */
  useEffect(() => {
    if (!symbol) { setDaily([]); return undefined; }
    let live = true;
    setDaily([]);
    void api.bars(symbol, '1d', 2)
      .then((r) => { if (live) setDaily(r.bars); })
      .catch(() => { if (live) setDaily([]); });
    return () => { live = false; };
  }, [symbol]);

  return {
    bars, barSeconds, daily, lastPrice, delayedByMs,
    dataSource, dataReason, marketStatus, loading,
    analysis, disclaimer, setAnalysis,
  };
}
